import { verificationMessages, turnstileLanguage } from './messages.js'

let scriptPromise = null
function failure(code) {
  const error = new Error(code)
  error.code = code
  error.name = code === 'cancelled' ? 'AbortError' : 'HumanVerificationError'
  error.humanVerification = true
  return error
}

export function loadTurnstile(win, doc) {
  if (win.turnstile && typeof win.turnstile.render === 'function') return Promise.resolve(win.turnstile)
  if (scriptPromise) return scriptPromise
  const current = new Promise((resolve, reject) => {
    const script = doc.createElement('script')
    let done = false
    const finish = error => {
      if (done) return
      done = true
      clearTimeout(timer)
      script.onload = script.onerror = null
      if (error) {
        if (script.parentNode) script.parentNode.removeChild(script)
        reject(error)
      } else resolve(win.turnstile)
    }
    const timer = setTimeout(() => finish(failure('unavailable')), 15000)
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.onload = () => finish(win.turnstile && typeof win.turnstile.render === 'function' ? null : failure('unavailable'))
    script.onerror = () => finish(failure('unavailable'))
    doc.head.appendChild(script)
  })
  scriptPromise = current
  current.catch(() => { if (scriptPromise === current) scriptPromise = null })
  return current
}

export function showTurnstile(config, { signal, locale = 'en', fullPage = false, win = window, doc = document } = {}) {
  if (signal && signal.aborted) return Promise.reject(failure('cancelled'))
  const text = verificationMessages(locale)
  return new Promise((resolve, reject) => {
    const overlay = doc.createElement('div')
    overlay.className = 'human-verification-overlay' + (fullPage ? ' human-verification-overlay--gate' : '')
    overlay.innerHTML = '<section class="human-verification-panel" role="dialog" aria-modal="true" aria-labelledby="human-verification-title" aria-describedby="human-verification-description"><div class="human-verification-mark" aria-hidden="true">✓</div><h2 id="human-verification-title" class="human-verification-title"></h2><p id="human-verification-description" class="human-verification-description"></p><div class="human-verification-widget"></div><p class="human-verification-status" role="status" aria-live="polite"></p><div class="human-verification-actions"><button type="button" class="human-verification-action human-verification-cancel"></button><button type="button" class="human-verification-action human-verification-retry" hidden></button></div></section>'
    const title = overlay.querySelector('h2')
    const description = overlay.querySelector('.human-verification-description')
    const container = overlay.querySelector('.human-verification-widget')
    const status = overlay.querySelector('.human-verification-status')
    const cancel = overlay.querySelector('.human-verification-cancel')
    const retry = overlay.querySelector('.human-verification-retry')
    title.textContent = text.title
    description.textContent = text.description
    cancel.textContent = text.cancel
    retry.textContent = text.retry
    const previousFocus = doc.activeElement
    const previousOverflow = doc.body.style.overflow
    const background = Array.from(doc.body.children).filter(node => node instanceof win.HTMLElement)
    const previousInert = background.map(node => node.inert)
    let api = null
    let widgetId = null
    let attempt = 0
    let settled = false
    let sizeMode = ''
    const widgetSize = () => container.clientWidth < 300 ? 'compact' : 'flexible'
    const onResize = () => {
      if (!settled && widgetId !== null && widgetSize() !== sizeMode) begin()
    }
    const resizeObserver = win.ResizeObserver ? new win.ResizeObserver(onResize) : null
    function removeWidget() {
      if (api && widgetId !== null) {
        try { api.remove(widgetId) } catch (_) {}
      }
      widgetId = null
      container.textContent = ''
    }
    function finish(error, token) {
      if (settled) return
      settled = true
      attempt += 1
      clearTimeout(timer)
      if (resizeObserver) resizeObserver.disconnect()
      win.removeEventListener('resize', onResize)
      removeWidget()
      if (signal) signal.removeEventListener('abort', abort)
      doc.removeEventListener('keydown', onKeyDown, true)
      background.forEach((node, index) => { node.inert = previousInert[index] })
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay)
      doc.body.style.overflow = previousOverflow
      if (previousFocus && previousFocus.isConnected && previousFocus.focus) previousFocus.focus()
      if (error) reject(error)
      else resolve(token)
    }
    const abort = () => finish(failure('cancelled'))
    function onKeyDown(event) {
      if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); abort() }
      if (event.key === 'Tab') {
        const focusable = Array.from(overlay.querySelectorAll('button:not([hidden]):not(:disabled), iframe'))
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (!overlay.contains(doc.activeElement)) { event.preventDefault(); first.focus() }
        else if (event.shiftKey && doc.activeElement === first) { event.preventDefault(); last.focus() }
        else if (!event.shiftKey && doc.activeElement === last) { event.preventDefault(); first.focus() }
      }
    }
    function showRetry(code, current, providerCode) {
      if (settled || current !== attempt) return
      attempt += 1
      removeWidget()
      // Keep only the public numeric error code; never render provider payloads or tokens.
      const reference = typeof providerCode === 'string' && /^\d{6}$/.test(providerCode) ? providerCode : ''
      status.textContent = (text[code] || text.unavailable) + (reference ? ' (' + reference + ')' : '')
      retry.hidden = false
      retry.focus()
    }
    function begin() {
      const current = ++attempt
      removeWidget()
      retry.hidden = true
      status.textContent = text.loading
      loadTurnstile(win, doc).then(loaded => {
        if (settled || current !== attempt) return
        api = loaded
        status.textContent = ''
        sizeMode = widgetSize()
        const id = api.render(container, {
          sitekey: config.siteKey,
          action: 'api_access',
          theme: 'light',
          size: sizeMode,
          language: turnstileLanguage(locale),
          retry: 'never',
          'response-field': false,
          callback(token) { if (!settled && current === attempt) finish(null, token) },
          'error-callback'(errorCode) { showRetry('unavailable', current, errorCode); return true },
          'expired-callback'() { showRetry('invalid', current) },
          'timeout-callback'() { showRetry('timeout', current) }
        })
        if (settled || current !== attempt) { try { api.remove(id) } catch (_) {} }
        else widgetId = id
      }).catch(() => showRetry('unavailable', current))
    }
    const timer = setTimeout(() => finish(failure('timeout')), 180000)
    cancel.onclick = abort
    retry.onclick = begin
    doc.body.appendChild(overlay)
    doc.body.style.overflow = 'hidden'
    background.forEach(node => { node.inert = true })
    doc.addEventListener('keydown', onKeyDown, true)
    if (signal) signal.addEventListener('abort', abort)
    cancel.focus()
    if (resizeObserver) resizeObserver.observe(container)
    win.addEventListener('resize', onResize)
    begin()
  })
}
